/**
 * @author        esraa.afifi@zumper.com (Esraa Afifi)
 * @copyright     Copyright (c) 2021, Zumper
 * @description   User Menu
 */

import { useRouteProps } from '@zcode-ui/hook'
import { Hamburger, UserAvatar } from '@zcode-ui/icons'
import { Menu, MenuTrigger, cn } from '@zcode-ui/shadz-ui'
import { isDetailPageNew, loadGlobalNavLinkpack } from '@zcode-ui/web-zumper-global-state'
import { LinkOrButton } from '@zcode-ui/web-zumper-ui'
import { escHandler } from '@zcode/a11y-utils'
import { globalNavLinkpack } from '@zcode/nr/es/shared/api'
import { routesConstants } from '@zcode/zumper-routes'
import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { TRACK_NAVBAR_ACTION, TRACK_USER_MENU_ACTION } from '../../constants'
import { useUserMenuContext } from '../../module/context/UserMenuContext'
import { selectIsBot } from '../../module/selectors'
import type { OnNavLinkClickProps, SubmenuType } from '../../types'
import { getPostListingLink } from '../../utils/getPostListingLink'
import { getPrevSubmenu } from '../../utils/submenu'
import { MobileMenu } from '../MobileMenu'
import { MenuListContentBot } from './MenuListContentBot'
import { MenuListContentLoadable } from './MenuListContentLoadable'
import type { MainUserSubmenuProps } from './submenus/main/MainUserSubmenu'

export type MainUserSubmenuPassedProps = Omit<MainUserSubmenuProps, 'handleSetActiveSubmenu'>

export interface UserMenuProps extends MainUserSubmenuPassedProps {
  isCurrentSearchView?: boolean
  isDetailView?: boolean
  isHomepageView?: boolean
  isLoggedIn?: boolean
  isMobile?: boolean
  onEscPressed?: (event: KeyboardEvent) => void
  onNavLinkClick?: (args: OnNavLinkClickProps) => void
  toolsActivated?: boolean
}

export const UserMenu = (props: UserMenuProps) => {
  const {
    isCurrentSearchView,
    isDetailView,
    isHomepageView,
    isMessage3,
    isMobile,
    onEscPressed,
    onLoginClick,
    onLogoutClick,
    onNavLinkClick,
  } = props satisfies UserMenuProps
  const { dispatchTrackUserMenuClick, isLoggedIn, toolsActivated } = useUserMenuContext()
  const isBot = useSelector(selectIsBot)
  const isNewDetailPage = useSelector(isDetailPageNew)
  const isProUser = isLoggedIn && toolsActivated
  const postListingLink = getPostListingLink({ isProUser })
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const [activeSubmenu, setActiveSubmenu] = useState<SubmenuType>('smMain')
  const showPrevMenu = () => setActiveSubmenu(getPrevSubmenu(activeSubmenu))
  const [renderMenuList, setRenderMenuList] = useState(isBot)
  const dispatch = useDispatch()
  const isAnyDetailPage = (isNewDetailPage || isDetailView) && !isCurrentSearchView

  const handleSetActiveSubmenu = useCallback(
    (navLinkClickProps: OnNavLinkClickProps, submenu: SubmenuType) => {
      onNavLinkClick?.(navLinkClickProps)
      setActiveSubmenu(submenu)
    },
    [onNavLinkClick]
  )

  const withCityTracking = (trackAction: string) => {
    return (name: string, href: string) => {
      onNavLinkClick?.({
        trackAction,
        trackCity: name,
        linkClicked: href,
      })
    }
  }

  const postListingColor = isHomepageView
    ? 'hover:text-gray-800 text-gray-950'
    : 'hover:text-gray-150 text-gray-800'

  const routeProps = useRouteProps()

  const handleMenuButtonMouseOver = () => {
    setRenderMenuList(true)
    dispatch(loadGlobalNavLinkpack({ api: globalNavLinkpack, routeProps }))
  }

  const onOpenHandler = () => {
    setActiveSubmenu('smMain')
    dispatchTrackUserMenuClick(TRACK_USER_MENU_ACTION.MENU_OPEN)
  }

  useEffect(() => {
    onEscPressed && window.document.addEventListener('keydown', escHandler(onEscPressed))
    return () => {
      onEscPressed && window.document.removeEventListener('keydown', escHandler(onEscPressed))
    }
  }, [onEscPressed])

  if (isBot && !isMobile) {
    return (
      <MenuListContentBot
        {...props}
        activeSubmenu={activeSubmenu}
        handleSetActiveSubmenu={handleSetActiveSubmenu}
        setActiveSubmenu={setActiveSubmenu}
        showPrevMenu={showPrevMenu}
        withCityTracking={withCityTracking}
      />
    )
  }

  if (isMobile && showMobileMenu) {
    return (
      <MobileMenu
        isCurrentSearchView={isCurrentSearchView}
        isDetailView={isAnyDetailPage}
        isHomepageView={false}
        isMessage3={isMessage3}
        onClose={() => setShowMobileMenu(false)}
        onLoginClick={onLoginClick}
        onLogoutClick={onLogoutClick}
        onNavLinkClick={onNavLinkClick}
        onSignupClick={props.onSignupClick}
        showModal
        signInHref={props.signInHref}
        signUpHref={props.signUpHref}
      />
    )
  }
  return (
    <div className="flex items-center">
      <div className={isCurrentSearchView || isAnyDetailPage ? 'hidden lg:flex' : 'flex'}>
        <LinkOrButton
          className={`${postListingColor} bg-transparent no-underline pr-[15px] text-sm whitespace-nowrap hover:text-gray-800`}
          href={routesConstants.ADVERTISE_URL}
          onClick={() => {
            onNavLinkClick?.({
              analyticsEvent: 'Advertise (header)',
              trackAction: TRACK_NAVBAR_ACTION.ADVERTISE,
            })
          }}
        >
          Advertise
        </LinkOrButton>
        <LinkOrButton
          className={`${postListingColor} bg-transparent text-sm no-underline whitespace-nowrap hover:text-gray-800`}
          href={postListingLink}
          onClick={() => {
            onNavLinkClick?.({
              trackAction: TRACK_NAVBAR_ACTION.POST_YOUR_RENTAL,
            })
          }}
        >
          Post a listing
        </LinkOrButton>
      </div>
      <Menu
        onOpenChange={(open) => {
          if (open) {
            onOpenHandler()
          }
        }}
      >
        <MenuTrigger
          aria-label="open side menu"
          className={cn(
            'flex items-center gap-2 w-16 h-8 ml-3 p-[6px] bg-transparent border border-solid border-gray-150 rounded-[20px] cursor-pointer hover:border-gray-150 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring focus-visible:border-neutral-500 focus-visible:outline-none focus-visible:border',
            !isHomepageView && 'bg-gray-0'
          )}
          onFocus={handleMenuButtonMouseOver}
          onMouseOver={handleMenuButtonMouseOver}
        >
          <UserAvatar />
          <Hamburger fill="#0D2A3A" size={14} />
        </MenuTrigger>
        {renderMenuList && (
          <MenuListContentLoadable
            {...props}
            activeSubmenu={activeSubmenu}
            handleSetActiveSubmenu={handleSetActiveSubmenu}
            setActiveSubmenu={setActiveSubmenu}
            showPrevMenu={showPrevMenu}
            withCityTracking={withCityTracking}
          />
        )}
      </Menu>
    </div>
  )
}
